import { Amount } from '@ui/components/Amount/Amount';
import { AmountUsd } from '@ui/components/Amount/AmountUsd';
import { usePayTokenPriceLatest } from '@ui/features/Trade/hooks/usePayTokenPriceLatest';
import { TradeHistoryResponseOperationTypeEnum } from '@ui/services/backend/generated';
import { TradeHistory } from '@ui/services/backend/hooks/useTradeHistory/type';
import { useMemo } from 'react';
import { Box, HStack, styled, VStack } from 'styled-system/jsx';

export const getShouldHideHistoryCollateral = (history: TradeHistory) => {
  return [
    TradeHistoryResponseOperationTypeEnum.DecreaseOrderCreated,
    TradeHistoryResponseOperationTypeEnum.DecreaseOrderCancelled,
    TradeHistoryResponseOperationTypeEnum.DecreaseOrderUpdated,
  ].includes(history.type);
};

export const HistoryCollateral = ({ history }: { history: TradeHistory }) => {
  const latestCollateralPrice = usePayTokenPriceLatest(history.tokenIn);

  // Show the actual token amount at this lifecycle stage:
  // - position exists (collateral set on-chain) → post-fee margin
  // - otherwise (Place Limit / Pending / Refunded) → gross amountIn the user locked
  const collateralUsd = useMemo(() => {
    const price = history.tokenInPrice ?? latestCollateralPrice;
    if (!price) return;
    const tokenAmount = history.collateral ?? history.amountIn;

    return tokenAmount.times(price);
  }, [
    history.collateral,
    history.amountIn,
    history.tokenInPrice,
    latestCollateralPrice,
  ]);

  if (getShouldHideHistoryCollateral(history)) {
    return <Box />;
  }

  return (
    <HStack gap={0.5} alignItems="center" justifyContent="flex-start">
      <styled.img src={history.lvToken.image} width="14px" height="14px" />
      <VStack ml={1} gap={0} alignItems="flex-start" justify="center">
        <AmountUsd
          value={collateralUsd}
          textStyle="numbers.small"
          color="v2.text.secondary"
          opts={{ prefix: '~$' }}
          data-cy="HistoryCollateralUsd"
          data-cy-value={collateralUsd}
        />
        <Amount
          value={history.collateral || history.amountIn}
          textStyle="numbers.xsmall"
          color="v2.text.muted"
          opts={{
            displayDecimals: 2,
            suffix: ` ${history.tokenIn.displaySymbol}`,
          }}
          data-cy="HistoryCollateralToken"
          data-cy-value={history.collateral}
        />
      </VStack>
    </HStack>
  );
};
